import { useIsDataPreparationReadOnly } from "@/data-preparation-tool/hooks/use-is-data-preparation-read-only";
import { selectIsSavingHierarchyChanges } from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-selectors";
import { selectRootEntity } from "@/data-preparation-tool/store/revision/revision-entity-selectors";
import { isEntityCluster } from "@/data-preparation-tool/utils/entity-utils";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { useAppSelector } from "@/store/store-hooks";
import { grey } from "@faro-lotv/flat-ui";
import {
  CaptureTreeEntityRevision,
  CaptureTreeEntityType,
  CaptureTreeEntityTypeHint,
} from "@faro-lotv/service-wires";
import { Divider, useMediaQuery } from "@mui/material";
import { CSSProperties, JSX, useCallback, useRef, useState } from "react";
import { DeleteEntitiesDialog } from "../data-preparation-dialogs/delete-entities-dialog";
import {
  ScanTreeContextMenuAction,
  ScanTreeContextMenuActionItem,
} from "./scan-tree-context-menu-action-items/scan-tree-context-menu-action-item";
import {
  ScanTreeContextMenuBase,
  ScanTreeContextMenuBaseHandle,
} from "./scan-tree-context-menu-base";

export interface ScanTreeContextMenuProps {
  /**
   * Whether the context-menu icon should be visible
   *
   * @default true
   */
  visible?: boolean;

  /**
   * Whether the context-menu icon should be disabled
   *
   * @default false
   */
  disabled?: boolean;

  /** Callback executed when the menu is opened/closed */
  onToggle?(open: boolean): void;

  /**
   * Enable the dark version of the menu
   *
   * @default false
   */
  dark?: boolean;

  /** Optional color for mouse hover of the ellipsis icon used to open the menu; undefined to use the color inherited from parent */
  iconHoverColor?: CSSProperties["color"];

  /** The entity for which the context menu is being displayed. */
  entity?: CaptureTreeEntityRevision;

  /** Callback executed when the context menu is closed */
  onCloseContextMenu?(): void;

  /** Callback executed to expand a tree node by ID. */
  onExpandNode?(entityId: CaptureTreeEntityRevision["id"]): void;
}

/**
 * @returns a context menu for the node in registration capture tree
 */
export function ScanTreeContextMenu({
  visible,
  disabled,
  onToggle,
  entity,
  onCloseContextMenu,
  onExpandNode,
  dark = false,
}: ScanTreeContextMenuProps): JSX.Element | null {
  const menuRef = useRef<ScanTreeContextMenuBaseHandle>(null);
  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState(false);
  const rootEntity = useAppSelector(selectRootEntity);
  const supportsHovering = useMediaQuery("(hover: hover)");
  const isSaving = useAppSelector(selectIsSavingHierarchyChanges);
  const isReadOnly = useIsDataPreparationReadOnly();

  const isRegistrationDev = useAppSelector(
    selectHasFeature(Features.RegistrationDev),
  );
  const isSimpleScanOrganization = useAppSelector(
    selectHasFeature(Features.SimpleScanOrganization),
  );

  const cleanupOnContextMenuClose = useCallback(() => {
    menuRef.current?.closeContextMenu();
    onCloseContextMenu?.();
  }, [menuRef, onCloseContextMenu]);

  const handleDeleteAction = useCallback(() => {
    cleanupOnContextMenuClose();
    setIsDeletionDialogOpen(true);
  }, [cleanupOnContextMenuClose]);

  if (!entity) {
    return null;
  }

  // Orbis and SiteScape captures are special clusters that are always internally registered
  // and must never be manually locked or unlocked.
  const isSpecialCaptureCluster =
    entity.typeHint === CaptureTreeEntityTypeHint.orbisCapture ||
    entity.typeHint === CaptureTreeEntityTypeHint.siteScapeCapture;
  const showLockClusterAction =
    isRegistrationDev &&
    entity.type === CaptureTreeEntityType.cluster &&
    !isSpecialCaptureCluster;
  const showClusterRelatedActions =
    isSimpleScanOrganization &&
    isEntityCluster(entity) &&
    !isSpecialCaptureCluster;

  const contextMenuItems =
    entity.id === rootEntity?.id
      ? [
          isSimpleScanOrganization ? (
            <ScanTreeContextMenuActionItem
              key="add-cluster-root"
              action={ScanTreeContextMenuAction.addCluster}
              entity={entity}
              onContextMenuItemActionPerformed={cleanupOnContextMenuClose}
              onExpandNode={onExpandNode}
              disabled={isSaving || isReadOnly}
            />
          ) : null,

          isSimpleScanOrganization ? (
            <ScanTreeContextMenuActionItem
              key="sort-descendants-root"
              action={ScanTreeContextMenuAction.sortDescendants}
              entity={entity}
              onContextMenuItemActionPerformed={cleanupOnContextMenuClose}
              disabled={isSaving || isReadOnly}
            />
          ) : null,
        ]
      : [
          <ScanTreeContextMenuActionItem
            key="edit-name"
            action={ScanTreeContextMenuAction.editName}
            entity={entity}
            onContextMenuItemActionPerformed={cleanupOnContextMenuClose}
            disabled={isSaving || isReadOnly}
          />,

          showClusterRelatedActions ? (
            <ScanTreeContextMenuActionItem
              key="add-cluster"
              action={ScanTreeContextMenuAction.addCluster}
              entity={entity}
              onContextMenuItemActionPerformed={cleanupOnContextMenuClose}
              onExpandNode={onExpandNode}
              disabled={isSaving || isReadOnly}
            />
          ) : null,

          showClusterRelatedActions ? (
            <ScanTreeContextMenuActionItem
              key="sort-descendants"
              action={ScanTreeContextMenuAction.sortDescendants}
              entity={entity}
              onContextMenuItemActionPerformed={cleanupOnContextMenuClose}
              disabled={isSaving || isReadOnly}
            />
          ) : null,

          // The visibility can be toggled when hovering over the entity in the tree already, if supported
          supportsHovering ? null : (
            <ScanTreeContextMenuActionItem
              key="visibility"
              action={ScanTreeContextMenuAction.visibility}
              entity={entity}
              onContextMenuItemActionPerformed={cleanupOnContextMenuClose}
            />
          ),

          <ScanTreeContextMenuActionItem
            key="focus-on"
            action={ScanTreeContextMenuAction.focusOn}
            entity={entity}
            onContextMenuItemActionPerformed={cleanupOnContextMenuClose}
          />,

          showLockClusterAction ? (
            <ScanTreeContextMenuActionItem
              key="lock-cluster"
              action={ScanTreeContextMenuAction.lockCluster}
              entity={entity}
              onContextMenuItemActionPerformed={cleanupOnContextMenuClose}
              disabled={isSaving || isReadOnly}
            />
          ) : null,

          <Divider
            key="divider"
            sx={{
              mt: 1,
              mb: 1,
              borderColor: dark ? grey[800] : grey[200],
            }}
          />,
          <ScanTreeContextMenuActionItem
            key="delete"
            action={ScanTreeContextMenuAction.delete}
            entity={entity}
            onContextMenuItemActionPerformed={handleDeleteAction}
            disabled={isSaving || isReadOnly}
          />,
        ];

  return (
    <>
      <ScanTreeContextMenuBase
        ref={menuRef}
        disabled={disabled}
        visible={visible}
        onToggle={onToggle}
        onClose={onCloseContextMenu}
      >
        {contextMenuItems}
      </ScanTreeContextMenuBase>
      {/*  The div wrapper is necessary to prevent dialog interaction events from
      bubbling into the tree node click handler. This is because the context
      menu is rendered as a child of the tree node, so any click outside the
      dialog will propagate to the tree node and trigger unwanted side effects
      such as toggling visibility or selecting the node. */}
      <div
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <DeleteEntitiesDialog
          entity={entity}
          isDeletionDialogOpen={isDeletionDialogOpen && !isReadOnly}
          onClose={() => {
            setIsDeletionDialogOpen(false);
          }}
        />
      </div>
    </>
  );
}
